import { useState, useRef, useEffect } from "preact/hooks";
import { useSort } from "@nosto/search-js/preact/hooks";
import { sortOptions } from "@/config";
import { cl } from "@nosto/search-js/utils";

type Option = {
  value: string;
  label: string;
  className?: string;
};

type Props = {
  options: Option[];
  value?: string;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
};

export default function CustomSelect({
  options,
  value,
  placeholder = "Sort",
  className = "",
  onChange,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string | undefined>(value);
  const { activeSort, setSort } = useSort(sortOptions);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (value !== undefined) {
      setSelected(activeSort);
    }
  }, [value]);

  const selectedOption = options.find((opt) => opt.value === selected);

  function handleSelect(val: string) {
    setSelected(val);
    setIsOpen(false);
    onChange?.(val);
  }

  return (
    <div ref={wrapperRef} className={className}>
      {/* Hidden native select (for forms / accessibility) */}
      <select
        value={selected}
        onChange={(e) => setSort((e.target as HTMLSelectElement)?.value)}
        className="hidden"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <div className={cl("select-ui", isOpen && "select-ui--is-open")}>
        <button
          type="button"
          onClick={() => setIsOpen((p) => !p)}
          className="select-ui__trigger"
        >
          <span className="block mobile-text !mr-1 md:hidden max-md:text-[13px]">
            {placeholder}
          </span>
          <span className="hidden md:block !text-[13px] font-light leading-tight">
            {selectedOption?.label || ""}
          </span>
          <span className="br-icon br-icon--sm" aria-hidden="true">
            <svg>
              <use href="#chevron-down" />
            </svg>
          </span>
        </button>

        <div className="select-ui__options">
          {options.map((opt) => {
            const isSelected = opt.value === selected;

            return (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`
                    select-ui__options-item
                    ${isSelected ? "selected" : ""}
                  `}
                data-value={opt.value}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
